/**
 * Mulligan swap TanStack Query mutation hook.
 *
 * Wraps the apiClient's `applyMulliganSwap` helper. A fresh UUID v4
 * Idempotency-Key is generated per invocation and forwarded as the HTTP
 * `Idempotency-Key` header so retries with identical bodies collapse into
 * a single backend transaction (per the design's idempotency contract).
 *
 * On success we invalidate three caches whose data the swap mutates:
 *   - `['picks', poolId, userId]` — the player's roster changed.
 *   - `['mulligan-status', poolId]` — allowance/locked-pick state changed.
 *   - `['leaderboard', poolId]`    — scoring is recomputed post-swap.
 *
 * Requirements: 8.1, 11.1
 * Design: Frontend Components → TanStack Query Hooks; Scoring Continuity
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { MulliganSwapRequest } from '@/services/api';
import { MulliganApiError } from '@/services/api/mulligan.errors';
import { mulliganStatusKeys } from './useMulliganStatus';
import { leaderboardKeys } from '../useLeaderboard';
import { pickKeys } from '../usePicks';

/**
 * Generate a v4 UUID using the WebCrypto API.
 *
 * `crypto.randomUUID()` is supported in all modern browsers (and Node 19+
 * via the `node:crypto` `webcrypto` namespace, which Vitest's jsdom
 * environment exposes). Wrapping it lets tests override the value.
 */
function generateIdempotencyKey(): string {
  return crypto.randomUUID();
}

/**
 * Hook to apply a mulligan swap for the current user in a pool.
 *
 * @param poolId Pool identifier
 * @param userId The current player's userId. Used purely to scope the
 *   `picks` query invalidation; the backend derives the swapper's userId
 *   from the JWT.
 */
export function useMulliganSwap(poolId: string, userId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: MulliganSwapRequest) => {
      // Generate a fresh v4 UUID per invocation. React Query handles retries
      // by replaying mutationFn, which means each retry of the mutation also
      // gets a unique key — that is intentional, because the backend treats
      // a successful swap as a non-idempotent state mutation. The
      // Idempotency-Key only suppresses *exact* in-flight duplicates.
      const idempotencyKey = generateIdempotencyKey();
      const response = await api.mulligan.applyMulliganSwap(
        poolId,
        input,
        idempotencyKey
      );
      if (response.error) {
        throw new MulliganApiError(response.error);
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['picks', poolId, userId],
      });
      // Also invalidate the existing pickKeys cache shape used by
      // `useMyPicks` so the player's roster view refreshes.
      queryClient.invalidateQueries({
        queryKey: pickKeys.myPicks(poolId),
      });
      queryClient.invalidateQueries({
        queryKey: mulliganStatusKeys.pool(poolId),
      });
      queryClient.invalidateQueries({
        queryKey: leaderboardKeys.pool(poolId),
      });
    },
  });
}
